/**
 * tours.js
 */

import React from 'react';
import { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { paths, recordListPaths } from 'utils/routing';
import analytics from 'utils/analytics';
import { TourContext } from 'contexts/tour/tour-context';
import recordsPageSteps from 'components/tour/tour-data/records-page-steps';
import eventEditorSteps from 'components/tour/tour-data/event-editor-steps';
import exercisePartsSteps from 'components/tour/tour-data/exercise-parts-steps';
import exerciseTemplateSteps from 'components/tour/tour-data/exercise-template-steps';
import exerciseApplicabilitySteps from 'components/tour/tour-data/exercise-applicability-steps';
import exerciseEditButtonSteps from 'components/tour/tour-data/exercise-edit-button-steps';
import recordListSteps from 'components/tour/tour-data/record-list-steps';


export default function useTours() {
  const {
    activeTour,
    setActiveTour,
    setRunTour,
    setTourStep,
    setToursSeen,
    updateBeaconViews,
    resetTours,
  } = React.useContext(TourContext);


  const tourData = [
    {
      key: 0,
      name: 'Records Page Tour',
      icon: 'tour',
      steps: recordsPageSteps,
      callback: handleTourCallback,
      route: {
        path: recordListPaths,
        exact: true
      }
    },
    {
      key: 1,
      name: 'Event Editor Tour',
      icon: 'tour',
      steps: eventEditorSteps,
      callback: handleTourCallback,
      route: {
        path: [
          `/records(/.+)*${paths.training.base}${paths.training.wizardCreateEvent.path}`,
          `/records(/.+)*${paths.training.base}${paths.training.wizardEditEvent.path}`,
          `/records(/.+)*${paths.training.base}${paths.training.editEvent.path}`,
        ]
      }
    },
    {
      key: 2,
      name: 'Exercise Editor Tour',
      icon: 'tour',
      steps: exercisePartsSteps,
      callback: handleTourCallback,
      route: {
        path: [
          `/records(/.+)*${paths.training.base}${paths.training.wizardAddExercise.path}`,
          `/records(/.+)*${paths.training.base}${paths.training.wizardEditExercise.path}`,
          `/records(/.+)*${paths.training.base}${paths.training.addExercise.path}`,
          `/records(/.+)*${paths.training.base}${paths.training.editExercise.path}`,
        ],
      }
    },
    {
      key: 3,
      name: 'Exercise Templates Tour',
      icon: 'tour',
      steps: exerciseTemplateSteps,
      callback: handleTourCallback,
      route: {
        path: [
          `/records(/.+)*${paths.training.base}${paths.training.wizardAddExercise.path}`,
          `/records(/.+)*${paths.training.base}${paths.training.wizardEditExercise.path}`,
          `/records(/.+)*${paths.training.base}${paths.training.addExercise.path}`,
          `/records(/.+)*${paths.training.base}${paths.training.editExercise.path}`,
        ],
      }
    },
    {
      key: 4,
      name: 'Exercise Dog Applicability Tour',
      icon: 'tour',
      steps: exerciseApplicabilitySteps,
      callback: handleTourCallback,
      route: {
        path: [
          `/records(/.+)*${paths.training.base}${paths.training.wizardAddExercise.path}`,
          `/records(/.+)*${paths.training.base}${paths.training.wizardEditExercise.path}`,
          `/records(/.+)*${paths.training.base}${paths.training.addExercise.path}`,
          `/records(/.+)*${paths.training.base}${paths.training.editExercise.path}`,
        ],
      }
    },
    {
      key: 5,
      name: 'Exercise Edit Button Tour',
      icon: 'tour',
      steps: exerciseEditButtonSteps,
      callback: handleTourCallback,
      route: {
        path: 
        [ 
          `/records(/.+)*${paths.training.base}${paths.training.wizardCompleteExercise.path}`,
          `/records(/.+)*${paths.training.base}${paths.training.viewExercise.path}`,
        ]
      }
    },
    {
      key: 6,
      name: 'Record List Tour',
      icon: 'tour',
      steps: recordListSteps,
      callback: handleTourCallback,
      route: {
        path: recordListPaths,
        exact: true
      }
    },
  ];


  const startTour = React.useCallback((tourID) => {
    console.log(`starting tour ${tourID}`);
    setActiveTour(tourID);
    setRunTour(false);
    setTimeout(() => {
      setTourStep(0);
      setRunTour(true);
    }, 1);
  }, [setActiveTour, setRunTour, setTourStep]);


  const resetAllTours = React.useCallback(() => {
    resetTours();
  }, [resetTours]);


  const stopTour = React.useCallback(() => {
    setRunTour(false);
    setActiveTour(-1);
    setTourStep(-1);
  }, [setActiveTour, setRunTour, setTourStep]);


  function handleTourCallback(data) {
    const { action, index, type, status } = data;
    const tourName = `${tourData[activeTour].name}`;
    const isLastStep = index === data.size - 1;
    const analyticsStep = (index === 0) ? 'TourStart' : isLastStep ? 'TourEnd' : '';
    const isTourDone = [STATUS.FINISHED, STATUS.SKIPPED].includes(status) || (action === ACTIONS.CLOSE && isLastStep);

    if (action === ACTIONS.CLOSE && !isLastStep) {
      setTourStep(0);
    } else if (isTourDone) {
      setToursSeen(activeTour);
      stopTour();
      if (action === ACTIONS.SKIP) {
        analytics.trackGA4('event', { name: 'tour_use', params: { interaction_type: 'TourSkip', tour_name: tourName } });
      }
    } else if (type === EVENTS.TOOLTIP) {
      if (analyticsStep) {
        analytics.trackGA4('event', { name: 'tour_use', params: { interaction_type: analyticsStep, tour_name: tourName } });
      }
    } else if (type === EVENTS.STEP_AFTER) {
      setTourStep(index + 1);
    } else if (type === EVENTS.BEACON && status === STATUS.RUNNING) {
      updateBeaconViews(activeTour);
    }
  };

  return {startTour, stopTour, resetAllTours, tourData};
}